import './App.css'
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from "./hooks/useAuth";
import CaseStudyGenerator from "./components/CaseStudyGenerator";
import QuestionnaireForm from "./components/QuestionnaireForm";
import AuthCallback from "./components/AuthCallback";
import AuthErrorHandler from "./components/AuthErrorHandler";
import AutoCacheClear from "./components/AutoCacheClear";
import PreviewPage from "./components/PreviewPage";
import riscpointLogo from './assets/images/riscpoint_shield.svg';

if (import.meta.env.DEV) {
  import('./utils/authErrorTest.js');
}

interface LoadingWithTimeoutProps {
  message?: string;
}

const LoadingWithTimeout = ({ message = "Loading..." }: LoadingWithTimeoutProps) => {
  const { clearAuthCache, login } = useAuth();
  const [showClearOption, setShowClearOption] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowClearOption(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClearCache = async () => {
    setIsClearing(true);
    try {
      clearAuthCache();
      await new Promise<void>(resolve => setTimeout(resolve, 500));
      await login();
    } catch (error) {
      console.error('Error clearing cache:', error);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white bg-opacity-90">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">{message}</p>
        
        {showClearOption && !isClearing && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
            <p className="text-sm text-yellow-800 mb-3">
              Taking longer than usual? This might be due to a recent deployment.
            </p>
            <button
              onClick={handleClearCache}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
            >
              Clear Cache & Retry
            </button>
          </div>
        )}
        
        {isClearing && (
          <div className="mt-4">
            <p className="text-sm text-gray-500">Clearing cache and re-authenticating...</p>
          </div>
        )}
      </div>
    </div>
  );
};

const MainApp = () => {
  const { isAuthenticated: _isAuthenticated, isLoading: _isLoading, login: _login } = useAuth();

  const devBypassAuth = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
  const mockUser = { name: 'Dev User', email: 'dev@riscpoint.com' };

  if (_isLoading && !devBypassAuth) {
    return <LoadingWithTimeout message="Loading..." />;
  }

  if (!_isAuthenticated && !devBypassAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white bg-opacity-90">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Sign In Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in with your organization account to access the Case Study Generator.
            </p>
            <button
              onClick={_login}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium"
            >
              Sign In with Microsoft
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {devBypassAuth && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-yellow-800">
                🚧 DEV MODE - Signed in as: <span className="font-medium">{mockUser.name}</span> ({mockUser.email})
              </span>
            </div>
            <span className="text-xs text-yellow-600">
              Auth bypassed for UI testing
            </span>
          </div>
        </div>
      )}

      <CaseStudyGenerator />
    </div>
  );
};

const QuestionnaireApp = () => {
  const { isAuthenticated: _isAuthenticated, isLoading: _isLoading, login: _login } = useAuth();

  const devBypassAuth = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
  const mockUser = { name: 'Dev User', email: 'dev@riscpoint.com' };

  if (_isLoading && !devBypassAuth) {
    return <LoadingWithTimeout message="Loading questionnaire..." />;
  }

  if (!_isAuthenticated && !devBypassAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white bg-opacity-90">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Sign In Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in with your organization account to access the Case Study Generator.
            </p>
            <button
              onClick={_login}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium"
            >
              Sign In with Microsoft
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {devBypassAuth && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-yellow-800">
                🚧 DEV MODE - Signed in as: <span className="font-medium">{mockUser.name}</span> ({mockUser.email})
              </span>
            </div>
            <span className="text-xs text-yellow-600">
              Auth bypassed for UI testing
            </span>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              ← Back to File Upload
            </a>
            <span className="text-gray-300">|</span>
            <span className="text-sm text-gray-600">Questionnaire Form</span>
          </div>
        </div>
      </div>

      <QuestionnaireForm />
    </div>
  );
};

const PreviewApp = () => {
  const { isAuthenticated: _isAuthenticated, isLoading: _isLoading, login: _login } = useAuth();

  const devBypassAuth = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

  if (_isLoading && !devBypassAuth) {
    return <LoadingWithTimeout message="Loading preview..." />;
  }

  if (!_isAuthenticated && !devBypassAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white bg-opacity-90">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Sign In Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in with your organization account to access the preview.
            </p>
            <button
              onClick={_login}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium"
            >
              Sign In with Microsoft
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <PreviewPage />;
};

const AppRouter = () => {
  const currentPath = window.location.pathname;

  let PageComponent: React.ReactNode;

  if (currentPath === '/auth/callback') {
    PageComponent = <AuthCallback />;
  }
  else if (currentPath === '/questionnaire') {
    PageComponent = <QuestionnaireApp />;
  }
  else if (currentPath.startsWith('/preview')) {
    PageComponent = <PreviewApp />;
  }
  else {
    PageComponent = <MainApp />;
  }

  return (
    <>
      {!currentPath.startsWith('/preview') && (
        <img src={riscpointLogo} alt="RISCPoint Shield" className="app-logo" />
      )}
      {PageComponent}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppRouter />
      <AuthErrorHandler />
      <AutoCacheClear />
    </AuthProvider>
  );
}

export default App;
